import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StorageService implements OnDestroy {
  private subject: Subject<{ key: string; value: any }>;
  private listener: (event: StorageEvent) => void;

  constructor() {
    this.subject = new Subject();
    this.listener = (event) => {
      const { key, newValue: value } = event;
      this.subject.next({
        key: this.removeVersionPrefix(key || ''),
        value: value ? JSON.parse(value) : null,
      });
    };
    window.addEventListener('storage', this.listener);
  }

  private addVersionPrefix(key: string): string {
    return `v${environment.storage.version}/${key}`;
  }

  private removeVersionPrefix(key: string): string {
    return key.replace(`v${environment.storage.version}/`, '');
  }

  public put(key: string, value: any) {
    localStorage.setItem(this.addVersionPrefix(key), JSON.stringify(value));
    this.subject.next({ key, value });
  }

  public get(key: string, defaultValue: any = null): any {
    const value = localStorage.getItem(this.addVersionPrefix(key));

    if (value) return JSON.parse(value);
    return defaultValue;
  }

  public remove(key: string): any {
    localStorage.removeItem(this.addVersionPrefix(key));
    this.subject.next({ key, value: null });
  }

  public clear() {
    Object.keys(localStorage).forEach((key) =>
      this.remove(this.removeVersionPrefix(key))
    );
  }

  public subscribe(
    callback: (event: { key: string; value: any }) => void,
    keys: Array<string> = [],
    nullValues: boolean = false
  ): Subscription {
    return this.subject.asObservable().subscribe((pair) => {
      if (
        (!keys.length || keys.indexOf(pair.key) >= 0) &&
        (nullValues || pair.value)
      ) {
        callback(pair);
      }
    });
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.listener);
  }
}
